import { mountLoader } from './resourced-loader/resourced-loader.js';

// The screen the app shows over itself while it boots.
//
// This module is its own entry, loaded from a script tag in index.html ahead of
// the app's. That is deliberate and it is the whole reason the cloud appears
// when it does: static imports are evaluated before the importing module's body
// runs, so starting the loader from entry-client.ts meant waiting for Vue,
// axios, Sentry and every store to be fetched and evaluated first. Its own
// entry waits for three files instead, and they are the three it draws with.
//
// Which is why it sits outside the app's graph, in its own directory, and is
// emitted as its own chunk by the bootScreenEntry plugin in vite.config.ts
// rather than being given a script tag in index.html. Vite folds every module
// script in an HTML file into one entry chunk, so that tag would not be a
// second entry — it would be the same 1.7MB of Vue, axios and Sentry under a
// different name, with no sign of it in dev, where the tags stay separate.
//
// Nothing here may import the app, or the two are one graph again and the chunk
// grows back. That is also why the app reaches this through window rather than
// through an import — see lib/boot-screen.js, the app-side half of the
// handshake. An import would let Rollup find the shared module and split it out
// into a chunk this one would then have to wait for.
//
// The ground and the caption stay in index.html, painted before even this has
// been fetched. The cloud joins them when it arrives.

const HOST_ID = 'preload-spinner';

// How long the ground takes to fade once the cloud has gone. Matches the
// transition on #preload-spinner in index.html.
const FADE_MS = 400;

// A backgrounded tab freezes requestAnimationFrame, so the wait for a painted
// frame would never resolve and the screen would never leave. Past this, go
// anyway: a reveal one frame early is invisible, a screen that never leaves is
// not.
const PAINT_TIMEOUT_MS = 1000;

// The two shapes the screen comes in, and the whole of the difference between
// them.
//
// `first` is the one worth watching: the cloud gathers out of nothing, holds,
// and disperses. `repeat` skips only the gathering — the mark is assembled from
// the first frame and fades up already whole, then leaves on exactly the same
// disperse. Nothing moves faster in it. That matters more than the time saved:
// a gather compressed into a third of its duration does not read as a shorter
// entrance, it reads as the same entrance played at speed, which looks like a
// fault rather than a choice.
//
// A repeat load is not given nothing, because nothing is what a blank ground
// looks like, and a boot that shows a blank ground for half a second looks like
// a page that failed rather than one that loaded.
//
// `floor` is how long the screen is on for at minimum, counted from the
// navigation rather than from when this module ran — the ground and the caption
// are painted from index.html, so the navigation is when the user actually
// started looking. A warm boot can be ready in a few hundred milliseconds, and a
// screen that appears and vanishes reads as a flicker rather than as a screen.
//
// The floor covers the whole life of the screen, exit included, so a fast boot
// gets a complete cycle rather than a hold with an exit stacked on top. That
// bounds it from below: this module's own fetch, then `appear`, then `exit` plus
// the ground's fade. Neither floor can be cut without cutting the animation it
// has to contain, which is why each sits beside the timings it contains.
//
// `first` is three seconds because it can afford to be watched: the cloud runs
// on its own thread now (see the loader's shell), so the seconds it is up are
// sixty frames a second whatever the boot is doing underneath. A floor is only
// worth spending when what it buys is worth looking at.
//
// `vocabulary` is how many distinct pills are rasterised into the atlas at
// startup, against the shipping default of 640. Each costs atlas time — measured
// 76ms at the default, 23ms at 120 — and buys legible variety: `readable` (0.006
// of the population, left at its default) is the share of motes drawn large
// enough to be read, and each of those takes a label no other mote has. A full
// screen runs around 43,000 pills and so asks for about 260 readable ones; 120
// is 15 a band across the eight. Both profiles hold a fully inked mark long
// enough to read one, so both pay for it.
const COMMON = { vocabulary: 120, density: 0.6 };

const PROFILES = {
  first: { ...COMMON, appear: 900, exit: 1100, floor: 3000, waitsForApp: true },
  repeat: { ...COMMON, appear: 350, gather: false, exit: 1100, floor: 1900, waitsForApp: false },
};

let loader = null;
let dismissed = null;
let gone = false;

// The day, local, as the key a visit is stamped under.
const SHOWN_KEY = 'resourced-boot-screen-shown';

function today() {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
}

/**
 * Whether this load gets the long screen.
 *
 * Once a day, not once a tab. The full cloud is worth a couple of seconds the
 * first time someone opens the app in the morning; it is an obstacle on the
 * eighth tab of the afternoon, and a tab is opened far more often than a day
 * begins. The stamp is in localStorage rather than sessionStorage precisely
 * because sessionStorage is per-tab, which is the thing being ruled out.
 *
 * Storage that throws or is cleared means showing it, which is the old
 * behaviour and the harmless direction to fail in.
 */
function isFirstVisitToday() {
  if (new URLSearchParams(location.search).get('boot-screen') === 'force') return true;

  try {
    const stamp = today();
    if (localStorage.getItem(SHOWN_KEY) === stamp) return false;
    localStorage.setItem(SHOWN_KEY, stamp);
  } catch {
    return true;
  }
  return true;
}

const {
  floor: MIN_ON_SCREEN_MS,
  waitsForApp: WAITS_FOR_APP,
  ...loaderOptions
} = isFirstVisitToday() ? PROFILES.first : PROFILES.repeat;

function start() {
  const host = document.getElementById(HOST_ID);
  if (!host || loader) return;

  try {
    loader = mountLoader(host, loaderOptions);
  } catch (e) {
    // The loader has its own fallbacks for a context it cannot get; this is for
    // the case where it cannot start at all. The ground and the caption stay,
    // the app boots, and the screen is dismissed by the same path as always.
    console.error('Boot screen failed to mount', e);
  }
}

/* Resolved the moment the app asks the screen to leave, which it does once its
   stores are seeded — so this is "the app is ready", as distinct from "the app
   has been drawn", which is appHasPainted() two frames later. Held as a latch
   rather than read from the call because the screen may start leaving before
   the call ever comes. */
let announceAppReady;
const appReady = new Promise((resolve) => {
  announceAppReady = resolve;
});

/**
 * Resolve once the screen has been up long enough, whether or not the app is
 * ready. performance.now() is measured from the navigation.
 */
function minimumTimeServed() {
  const holdFor = MIN_ON_SCREEN_MS - (loaderOptions.exit + FADE_MS) - performance.now();
  return holdFor > 0 ? new Promise((resolve) => setTimeout(resolve, holdFor)) : Promise.resolve();
}

/**
 * Resolve once the app underneath has actually been painted — not merely once
 * its data has arrived.
 *
 * The difference is the whole point of the screen. Dismissing on the data means
 * dispersing over a frame the browser has not drawn yet, so the cloud clears to
 * a blank page and the app snaps in behind it. Waiting for the paint means the
 * app is already there, whole, the moment the cloud starts to go.
 *
 * Two frames straddle that paint. Vue flushes its render queue in a microtask,
 * which runs before the next frame callback, so by the second frame the render
 * has been both flushed and drawn — no dependency on the framework from here.
 */
function appHasPainted() {
  return new Promise((resolve) => {
    let settled = false;
    const go = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    requestAnimationFrame(() => requestAnimationFrame(go));
    setTimeout(go, PAINT_TIMEOUT_MS);
  });
}

/**
 * Take the screen away: disperse the cloud, then — once there is an app behind
 * it — fade the ground and remove.
 *
 * The two waits are separate because they answer different questions. The cloud
 * may start leaving as soon as its floor is served: it is dissolving over an
 * opaque ground, so nothing shows through while it goes. The *reveal* is the
 * ground's fade, and only that has to wait for the app, because only that
 * uncovers it.
 *
 * Keeping them apart is what lets the repeat screen leave on its own clock. Tied
 * together, the disperse could not start until the app announced itself, which
 * on a warm boot is late enough to leave the mark holding still for a third of a
 * second — and the app's own final render lands in exactly that gap, so the hold
 * is where its longest frame is spent. A cloud already dispersing absorbs that
 * frame; a cloud sitting still displays it.
 *
 * Idempotent — the call sites behind lib/boot-screen.js are mutually exclusive
 * in principle and cheap to double up on in practice.
 *
 * `immediate` is for the paths with no workspace to load — there is nothing to
 * cover and the caption is not even true. It skips the minimum-time floor and
 * the cloud's own disperse, leaving on the ground's fade alone: a login page
 * should not wait out a second of dispersal it has no reason to watch.
 */
function leave({ immediate = false } = {}) {
  if (dismissed) return dismissed;

  const host = document.getElementById(HOST_ID);
  if (!host) {
    gone = true;
    dismissed = Promise.resolve();
    return dismissed;
  }

  dismissed = (immediate ? Promise.resolve() : minimumTimeServed())
    // finish() releases the WebGL context on its way out, so there is no
    // destroy() to pair with it. If the loader never mounted, there is nothing
    // to disperse and the ground just fades.
    .then(() => (loader && !immediate ? loader.finish() : undefined))
    .catch((e) => console.error('Boot screen exit failed', e))
    // Never uncover an app that is not there yet. On every path but a
    // self-driven exit this is already resolved and costs a microtask.
    .then(() => appReady)
    .then(() => appHasPainted())
    .then(() => {
      host.classList.add('is-leaving');
      return new Promise((resolve) => setTimeout(resolve, FADE_MS));
    })
    .then(() => {
      // The host goes first so that nothing the teardown does can keep it up.
      host.remove();
      // The canvas is inside the host, so the fade above took the cloud with it
      // and the immediate path never called finish(). Releasing is still not
      // optional: removing the host leaves the context and its frame loop alive.
      try {
        if (immediate) loader?.destroy();
      } catch (e) {
        console.error('Boot screen teardown failed', e);
      }
      loader = null;
      gone = true;
    });

  return dismissed;
}

/**
 * The app's way of saying it is ready and the screen may go.
 *
 * Returns a promise so a caller can wait for the screen to be gone, though none
 * currently do.
 */
function dismissBootScreen(options = {}) {
  announceAppReady();
  return leave(options);
}

/* The repeat screen does not wait to be told. It covers a boot that is already
   warm, and it is a greeting rather than a cover, so it begins its exit the
   moment its floor is served — before the app announces itself, and therefore
   before the app's own final render. The reveal still waits, inside leave().

   Scheduled at floor time rather than started now so that a path which dismisses
   immediately still wins: a login page reaching dismissBootScreen() first sets
   `dismissed`, and this then finds the screen already going and does nothing. */
if (!WAITS_FOR_APP) minimumTimeServed().then(() => leave());

/* The whole of the contract with the app, and deliberately one stable function:
   the less the two halves share, the less a change here can mean a change over
   there. lib/boot-screen.js is the other side of it. */
window.__resourcedBootScreen = { dismiss: dismissBootScreen };

// Nothing in the app is *guaranteed* to dismiss this. app.js does it on all
// three of its boot paths, but a throw before it reaches one of them — a store
// that will not seed, a bad snapshot — leaves the screen as the topmost element
// on the page, covering an app that is otherwise working, with no way past it
// but a reload. Long enough that a slow boot on a bad connection reaches its own
// dismissal first, short enough to be a page rather than a hang. Uncovering a
// half-loaded app is a poor outcome; never uncovering it is a worse one.
//
// A repeat screen has already called leave() by now and is parked on appReady
// inside it, so `dismissed` being set is not the same as the screen being gone.
// Announcing the app unparks that chain; dismissing is idempotent either way.
const BACKSTOP_MS = 30_000;

setTimeout(() => {
  if (gone) return;
  console.error('Boot screen timed out waiting for the app; dismissing itself');
  dismissBootScreen({ immediate: true });
}, BACKSTOP_MS);

// Side effect on load, because being early is the point. A module script is
// deferred, so the document is parsed by the time this runs and the host is
// there to mount on, even though the tag is in <head> and the host is not.
start();
